import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { Creation } from '../../../_enums/invoice';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getCustomPaginatorIntl } from './custom-mat-paginator-intl';
import { NgbDate, NgbDatepickerI18n, NgbDateStruct, NgbCalendar } from '@ng-bootstrap/ng-bootstrap';
import { CustomDatepickerI18n } from '../../../_helpers/index';
import { icons } from '../../../sharedcomp/constans/icons.constans';
import { BillingService, BillingDocument, BillingResponse } from '../services/billing.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NoDataTableMessage, ErrorTableMessage, BillingTypeName, BillingFileFormat, DateRangeErrorTableMessage } from './billing.constants';

@Component({
  selector: 'app-billing',
  templateUrl: './billing.component.html',
  styleUrls: [
    './billing.component.css',
    '../../../../assets/css/new-date-range-picker.css',
    '../../../../assets/css/new-buttons-theme.css'
  ],
  providers: [
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
    { provide: MatPaginatorIntl, useFactory: getCustomPaginatorIntl }
  ],
  animations: [
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate(200, style({ opacity: 1 }))])
    ])
  ]
})
export class BillingComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  icons = icons;
  BillingFileFormat = BillingFileFormat;
  showOverlay = false;
  dateButtonLabel: string = 'Selección por fecha';
  ddlCreation: any[];
  creationFormControl = new FormControl(0);
  displayMonths: number;
  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;
  // Document type filter
  docTypeFormControl = new FormControl(0);
  ddlDocType = [
    { value: 0, name: 'Todos', code: '' },
    { value: 1, name: BillingTypeName['03'], code: '03' },
    { value: 2, name: BillingTypeName['01'], code: '01' },
    { value: 3, name: BillingTypeName['07'], code: '07' },
    { value: 4, name: BillingTypeName['08'], code: '08' }
  ];
  filterDocTypeName: string;

  billingData = new MatTableDataSource<BillingDocument>([]);
  displayedColumns: string[] = ['fechaEmision', 'tipoDocumento', 'numeroFactura', 'estado', 'descargar'];
  selectedCustomDateRange: { from: NgbDateStruct, to: NgbDateStruct } | null = null;
  totalRecords: number = 0;
  pageSize: number = 10;
  pageSizeOptions: number[] = [5, 10, 25];

  private originalData: BillingDocument[] = [];
  private searchSubject = new Subject<string>();
  private currentSearch: string = '';
  private currentBillingType: string = '';

  public readonly noDataTableMessage = NoDataTableMessage;
  public readonly errorTableMessage = ErrorTableMessage;
  public readonly dateRangeErrorTableMessage = DateRangeErrorTableMessage;
  public isTableLoading: boolean = false;
  public showRetryBtn: boolean = false;
  public isDownloading: boolean = false;
  public downloadingDocument: BillingDocument | null = null;
  public isDateRangeError: boolean = false;

  constructor(
    private creation: Creation, 
    private http: HttpClient, 
    private calendar: NgbCalendar,
    private billingService: BillingService
  ) { }

  ngOnInit(): void {
    this.ddlCreation = this.creation._newCreation;
    this.displayMonths = window.innerWidth > 900 ? 2 : 1;
    
    this.billingData = new MatTableDataSource<BillingDocument>([]);
    this.getInitDateRange();
    this.loadBillingDocuments();
    // Configurar el observable de búsqueda
    this.searchSubject.pipe(
      debounceTime(300), // Esperar 300ms después de la última tecla presionada
      distinctUntilChanged() // Solo emitir si el valor ha cambiado
    ).subscribe((searchTerm: string) => {
      this.currentSearch = searchTerm;
      this.loadBillingDocuments();
    });
  }

  ngAfterViewInit() {
    this.billingData.paginator = this.paginator;
  }

  toggleDateRangePicker(): void {
    this.showOverlay = !this.showOverlay;
  }

  onDateSelection(date: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  markDisabled = (date: NgbDate, current: { month: number }) => {
    return date.month !== current.month;
  };

  isHovered(date: NgbDate): boolean {
    return !!(this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate));
  }

  isInside(date: NgbDate): boolean {
    return !!(this.toDate && date.after(this.fromDate) && date.before(this.toDate));
  }

  isRange(date: NgbDate): boolean {
    return !!(date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date));
  }

  isFromDate(date: NgbDate): boolean {
    return !!(this.fromDate && date.equals(this.fromDate));
  }

  isToDate(date: NgbDate): boolean {
    return !!(this.toDate && date.equals(this.toDate));
  }

  onSearchChange(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.searchSubject.next(searchTerm);
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.loadBillingDocuments();
  }

  private loadBillingDocuments(): void {
    this.isTableLoading = true;
    this.showRetryBtn = false;
    this.isDateRangeError = false;
    this.billingData = new MatTableDataSource<BillingDocument>([]);

    const companyId = localStorage.getItem('companyId');
    const startDateFormatted = this.fromDate ? 
      `${this.fromDate.year}-${this.fromDate.month.toString().padStart(2, '0')}-${this.fromDate.day.toString().padStart(2, '0')} 05:00:00` : 
      null;
    const endDateFormatted = this.toDate ? 
      `${this.toDate.year}-${this.toDate.month.toString().padStart(2, '0')}-${this.toDate.day.toString().padStart(2, '0')} 05:00:00` : 
      null;

    if (startDateFormatted && endDateFormatted && companyId) {
      // Calcular diferencia de días (inclusiva)
      const startDate = new Date(startDateFormatted);
      const endDate = new Date(endDateFormatted);
      const diffTime = endDate.getTime() - startDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

      if (diffDays > 31) {
        this.isDateRangeError = true;
        this.showRetryBtn = true;
        this.isTableLoading = false;
        return;
      }

      this.billingService.getBillingDocuments(
        companyId, 
        startDateFormatted, 
        endDateFormatted,
        this.currentSearch,
        this.currentBillingType,
        this.pageSize
      ).subscribe({
        next: (response: BillingResponse) => {
          this.originalData = response.data;
          this.billingData = new MatTableDataSource<BillingDocument>(response.data);
          this.billingData.paginator = this.paginator;
          this.totalRecords = response.records;
          this.isTableLoading = false;
        },
        error: (error) => {
          console.error('Error loading billing documents:', error);
          this.originalData = [];
          this.billingData = new MatTableDataSource<BillingDocument>([]);
          this.billingData.paginator = this.paginator;
          this.totalRecords = 0;
          this.isTableLoading = false;
          this.showRetryBtn = true;
        }
      });
    } else {
      this.originalData = [];
      this.billingData = new MatTableDataSource<BillingDocument>([]);
      this.billingData.paginator = this.paginator;
      this.totalRecords = 0;
      this.isTableLoading = false;
    }
  }

  selectDateOption(option: number): void {
    this.creationFormControl.patchValue(option);
    this.showOverlay = !this.showOverlay;
    if (option === 9) {
      if (this.fromDate && this.toDate) {
        this.dateButtonLabel = this.formatDate(this.fromDate) + ' - ' + this.formatDate(this.toDate);
        this.selectedCustomDateRange = { from: this.fromDate, to: this.toDate };
      }
    } else {
      // Calcular fechas para opciones predefinidas
      let startDate: Date | null = null;
      let endDate: Date | null = null;
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalizar tiempo

      switch (option) {
        case 0: // Hoy
          startDate = today;
          endDate = today;
          this.dateButtonLabel = 'Hoy';
          break;
        case 1: // Ayer
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 1);
          endDate = new Date(startDate);
          this.dateButtonLabel = 'Ayer';
          break;
        case 2: // Esta semana (Domingo como inicio)
          startDate = new Date(today);
          startDate.setDate(today.getDate() - today.getDay());
          endDate = today;
          this.dateButtonLabel = 'Esta semana';
          break;
        case 3: // La semana pasada (Domingo como inicio)
          startDate = new Date(today);
          startDate.setDate(today.getDate() - today.getDay() - 7);
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);
          this.dateButtonLabel = 'La semana pasada';
          break;
        case 4: // Este mes
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = today;
          this.dateButtonLabel = 'Este mes';
          break;
        case 5: // Mes pasado
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          this.dateButtonLabel = 'Mes pasado';
          break;
        case 6: // Este año
          startDate = new Date(today.getFullYear(), 0, 1);
          endDate = today;
          this.dateButtonLabel = 'Este año';
          break;
        case 7: // Año pasado
          startDate = new Date(today.getFullYear() - 1, 0, 1);
          endDate = new Date(today.getFullYear() - 1, 11, 31);
          this.dateButtonLabel = 'Año pasado';
          break;
        case 8: // Todo
          startDate = null;
          endDate = null;
          this.dateButtonLabel = 'Todo';
          break;
        default:
          startDate = null;
          endDate = null;
          this.dateButtonLabel = 'Selección por fecha';
      }

      // Convertir y almacenar las fechas calculadas
      const fromDateStruct = this.dateToNgbDateStruct(startDate);
      const toDateStruct = this.dateToNgbDateStruct(endDate);

      // Actualizar NgbDate para el picker
      this.fromDate = startDate ? NgbDate.from(fromDateStruct) : null;
      this.toDate = endDate ? NgbDate.from(toDateStruct) : null;

      // Guardar el rango como NgbDateStruct
      this.selectedCustomDateRange = { from: fromDateStruct, to: toDateStruct };
    }

    // Cargar documentos con las fechas seleccionadas
    this.loadBillingDocuments();
  }

  filterCreationDetails(option: number): void {
    
  }

  private formatDate(date: NgbDate): string {
    const d = date.day.toString().padStart(2, '0');
    const m = date.month.toString().padStart(2, '0');
    const y = date.year.toString();
    return `${d}/${m}/${y}`;
  }

  private dateToNgbDateStruct(date: Date): NgbDateStruct | null {
    return date ? { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() } : null;
  }

  /** Document type filter handlers */
  selectDocTypeOption(option: number): void {
    this.docTypeFormControl.patchValue(option);
    const selected = this.ddlDocType.find(d => d.value === option);
    this.filterDocTypeName = selected ? selected.name : '';
    
    // Actualizar el tipo de documento actual
    this.currentBillingType = selected && selected.code ? selected.code : '';
    
    // Cargar documentos con el nuevo filtro
    this.loadBillingDocuments();
  }

  filterDocumentType(option: number): void {
    const selectedType = this.ddlDocType.find(d => d.value === option);
    
    if (selectedType && selectedType.code) {
      // Filtrar por el código específico
      this.billingData.data = this.originalData.filter(doc => doc.billing_type === selectedType.code);
    } else {
      // Mostrar todos los documentos
      this.billingData.data = this.originalData.slice();
    }
  }

  downloadDocument(billingDoc: BillingDocument, fileFormat: string): void {
    const companyId = localStorage.getItem('companyId');
    if (!companyId) {
      return;
    }

    // Set downloading state
    this.isDownloading = true;
    this.downloadingDocument = billingDoc;

    // Convert the file format to lowercase
    const fileFormatLower = fileFormat.toLowerCase();

    this.billingService.downloadBillingDocument(companyId, billingDoc, fileFormatLower)
      .subscribe({
        next: (response: { blob: Blob, filename: string | null }) => {
          // Create object URL for the blob
          const url = window.URL.createObjectURL(response.blob);
          
          // Create download link
          const link = document.createElement('a');
          link.href = url;
          const documentId = localStorage.getItem('document_id') || '';
          
          // Establecer el nombre del archivo y el tipo de contenido
          if (fileFormatLower === BillingFileFormat.XML || fileFormatLower === BillingFileFormat.CDR) {
            const prefix = fileFormatLower === BillingFileFormat.CDR ? 'R-' : '';
            link.download = `${prefix}${documentId}-${billingDoc.billing_type}-${billingDoc.serie}-${billingDoc.correlative}.zip`;
            link.type = 'application/zip';
          } else {
            link.download = `${documentId}-${billingDoc.billing_type}-${billingDoc.serie}-${billingDoc.correlative}.${fileFormatLower}`;
          }
          
          // Trigger download
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          // Reset downloading state
          this.isDownloading = false;
          this.downloadingDocument = null;
        },
        error: (error) => {
          console.error('Error downloading document:', error);
          // Reset downloading state on error
          this.isDownloading = false;
          this.downloadingDocument = null;
        }
      });
  }

  public reloadTable(): void {
    this.loadBillingDocuments();
  }

  getBillingTypeName(type: string): string {
    return BillingTypeName[type as keyof typeof BillingTypeName] || BillingTypeName.Otro;
  }

  private getInitDateRange(): void {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const fromDateStruct = this.dateToNgbDateStruct(firstOfMonth);
    const toDateStruct = this.dateToNgbDateStruct(now);

    if (fromDateStruct && toDateStruct) {
      this.fromDate = NgbDate.from(fromDateStruct);
      this.toDate = NgbDate.from(toDateStruct);
      this.selectedCustomDateRange = { from: fromDateStruct, to: toDateStruct };
      this.dateButtonLabel = this.formatDate(this.fromDate) + ' - ' + this.formatDate(this.toDate);
    }
  }
}
